import { useCallback, useState } from 'react';

import {
    Button,
    Drawer,
    DrawerFooter,
    DrawerHeader,
    Field,
    Textarea,
    type ButtonSize,
} from '@/design-system';
import { InspectionReopenLockedError } from '@/entities/inspection';
import { orderAccessFor, sentMilestones, type Order } from '@/entities/order';
import { useSessionStore } from '@/shared/api';
import { useOnlineStatus } from '@/shared/offline';

import { reopenSurveyCopy as copy } from '../copy';
import { reopenableSurvey, useReopenSurvey } from '../useReopenSurvey';

export interface ReopenSurveyButtonProps {
    order: Order;
    /** `onDark` in de kop van een keuring, `outline` in de detailbalk van Keuringen. */
    variant?: 'onDark' | 'outline';
    size?: ButtonSize;
}

/**
 * "Opname heropenen": zet een ingediende opname weer open, met een reden die op de
 * tijdlijn komt, en loopt er daarna meteen in.
 *
 * Het staat er voor de eigenaar en voor de inspecteur van de opname, ook als het
 * rapport al verstuurd is - de API zegt per opname wie het mag (`canReopen`). Wat er
 * dan met het verstuurde rapport gebeurt, staat erbij: het blijft staan tot er een
 * nieuwe versie is vastgesteld, en daarna ziet de klant die via dezelfde link.
 */
export function ReopenSurveyButton({
    order,
    variant = 'outline',
    size = 'md',
}: ReopenSurveyButtonProps) {
    const online = useOnlineStatus();
    const session = useSessionStore((state) => state.session);
    const access = orderAccessFor(session);
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const close = useCallback(() => setOpen(false), []);
    const { reopen, preparing } = useReopenSurvey(order);
    const target = reopenableSurvey(order);

    if (!target) {
        return null;
    }

    const sent = sentMilestones(order).report;
    const error = reopen.error;

    return (
        <>
            <Button
                variant={variant}
                size={size}
                onClick={(event) => {
                    /* In de detailbalk zou de klik anders ook de rij eronder raken. */
                    event.stopPropagation();
                    setOpen(true);
                }}
            >
                {copy.button}
            </Button>

            <Drawer open={open} onClose={close} side="right" width={400} title={copy.title}>
                <DrawerHeader
                    icon="survey"
                    title={copy.title}
                    description={sent ? copy.explanationSent : copy.explanation}
                    onClose={close}
                    closeLabel={copy.close}
                />
                <form
                    className="flex flex-1 flex-col"
                    onSubmit={(event) => {
                        event.preventDefault();
                        reopen.mutate({ ...target, reason: reason.trim() });
                    }}
                >
                    <div className="flex flex-col gap-3 p-5">
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                            {copy.nextSteps}
                            {sent && access.money ? ` ${copy.moneyNote}` : ''}
                        </p>
                        <Field label={copy.reason} description={copy.reasonHint}>
                            <Textarea
                                rows={3}
                                maxLength={500}
                                value={reason}
                                placeholder={copy.reasonPlaceholder}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        </Field>
                        {online ? null : (
                            <p
                                role="status"
                                className="m-0 text-[13px] leading-[1.5] text-[var(--warn-text)]"
                            >
                                {copy.offline}
                            </p>
                        )}
                        {error ? (
                            <p
                                role="alert"
                                className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]"
                            >
                                {error instanceof InspectionReopenLockedError
                                    ? copy.cancelled
                                    : copy.failed}
                            </p>
                        ) : null}
                    </div>
                    <DrawerFooter>
                        <Button
                            type="submit"
                            variant="primary"
                            size="md"
                            disabled={!online || reason.trim().length < 3 || reopen.isPending}
                        >
                            {reopen.isPending
                                ? preparing
                                    ? copy.preparing
                                    : copy.busy
                                : copy.confirm}
                        </Button>
                        <Button variant="outline" size="md" onClick={close}>
                            {copy.cancel}
                        </Button>
                    </DrawerFooter>
                </form>
            </Drawer>
        </>
    );
}
